import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';

// Tela interna: o catálogo de relatórios de WhatsApp de TODOS os clientes, com o ciclo de
// vida, o agendamento vivo e a leitura.
//
// Criar, testar e aprovar continua sendo SÓ no MCP: são esses caminhos que dependem da
// validação de palavra reservada, e mantê-la em dois lugares é o jeito de um relatório
// chamado PARAR acabar sequestrando o opt-out.
//
// O que a tela faz é o que vem DEPOIS de existir: apagar e mexer em quem recebe. Nenhum dos
// dois cria gatilho novo, e os dois são exatamente o que alguém precisa quando um cliente
// liga dizendo que está recebendo coisa demais — que é a pergunta que esta tela existe para
// responder.
//
// A guarda NÃO é `role === 'admin'`: existem contas de CLIENTE com esse papel (mmdash,
// leroyadmin, gabriel-raviera...), e esta tela mostra a configuração de todos os clientes.
// Quem tranca é o `requireInterno` na ROTA do Express — esconder o item do menu não tranca
// nada.
//
// A coluna que justifica a tela é o CICLO. "Quais relatórios existem" alguém descobre no
// MCP; "qual está aprovado, disparando para quem e até quando" é a pergunta que aparece
// quando um cliente diz que está recebendo coisa demais.

const DIAS = { 1: 'seg', 2: 'ter', 3: 'qua', 4: 'qui', 5: 'sex', 6: 'sáb', 7: 'dom' };

const STATUS = {
  draft:    { rotulo: 'rascunho', cor: '#6b6a78', fundo: '#f2f2f5' },
  tested:   { rotulo: 'testado',  cor: '#8a5a00', fundo: '#fdf3e3' },
  approved: { rotulo: 'aprovado', cor: '#0f7a2e', fundo: '#e8f6ec' },
};

// DATA PURA é dia de CALENDÁRIO, não instante: "31/10" é 31/10 em qualquer fuso. Passá-la
// por `new Date` e converter para BRT a empurra um dia para trás (vira 30/10), porque é
// interpretada como meia-noite UTC. Por isso ela é formatada no texto, sem Date.
function fmtDia(iso) {
  const [a, m, d] = String(iso).split('-');
  return `${d}/${m}/${a.slice(2)}`;
}

// Todo TIMESTAMP da base é UTC e sem fuso na string. O `Z` diz isso ao parser; sem ele o
// navegador assume o fuso LOCAL de quem abre, e a mesma linha mostraria horas diferentes
// para gente em fusos diferentes.
function fmtData(iso, comHora = true) {
  if (!iso) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(iso))) return fmtDia(iso);
  try {
    const temFuso = iso.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(iso);
    const d = new Date(temFuso ? iso : iso + 'Z');
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit', month: '2-digit', year: '2-digit',
      ...(comHora ? { hour: '2-digit', minute: '2-digit' } : {}),
    });
  } catch { return iso; }
}

// O endereço do dashboard que o relatório manda, no MESMO ambiente em que a tela está
// aberta.
//
// O host NÃO é montado com 'alia.do' fixo: o primeiro rótulo carrega o ambiente
// (`admin`, `admin-qa`, `admin-hmg`), então a troca é do rótulo da EMPRESA preservando o
// sufixo — senão o link do admin de QA cairia em produção, que é o pior destino possível
// para um clique dado por engano.
//
// `/dash/<ref>` resolve por `url_path` OU por `slug` no apidash, então o `dashboard_slug`
// do relatório serve de referência direto, sem a tela precisar consultar o dashboard.
//
// Devolve null quando não dá para montar um endereço honesto (host sem ponto, como
// `localhost` no dev, ou relatório sem dashboard). Link quebrado é pior que link ausente.
function urlDoDashboard(r) {
  if (!r?.dashboard_slug || !r?.subdomain) return null;
  const host = String(window.location.hostname || '');
  const partes = host.split('.');
  if (partes.length < 2) return null;
  const sufixo = (partes[0].match(/-(qa|hmg)$/) || [])[0] || '';
  partes[0] = `${r.subdomain}${sufixo}`;
  return `${window.location.protocol}//${partes.join('.')}`
       + `/dash/${encodeURIComponent(r.dashboard_slug)}`;
}

// A palavra e o nome viram link para o dashboard. Em modo `dashboard` o link é LITERALMENTE
// o que chega no WhatsApp — a imagem é uma foto desta página. Nos outros modos o WhatsApp
// leva um PDF montado do endpoint, e aí o link é a origem do número, não o arquivo enviado:
// o `title` diz qual dos dois é, para ninguém concluir que viu o anexo.
function LinkDash({ r, children }) {
  const url = urlDoDashboard(r);
  if (!url) return children;
  const eImagem = (r.render || {}).modo === 'dashboard';
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
       title={eImagem
         ? 'Abrir o dashboard — é esta página que vira a imagem enviada no WhatsApp'
         : 'Abrir o dashboard de origem — o WhatsApp recebe um PDF montado do endpoint'}
       style={{ color: 'inherit', textDecoration: 'underline',
                textDecorationColor: '#c9c8d4', textUnderlineOffset: 3 }}>
      {children}
    </a>
  );
}

function Selo({ status }) {
  const s = STATUS[status] || { rotulo: status || '—', cor: '#6b6a78', fundo: '#f2f2f5' };
  // O rótulo vem junto com a cor, sempre: cor sozinha não carrega sentido.
  return (
    <span style={{ background: s.fundo, color: s.cor, padding: '2px 8px',
                   borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {s.rotulo}
    </span>
  );
}

function Tile({ titulo, valor, nota }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e7ee', borderRadius: 10,
                  padding: '14px 16px', minWidth: 150 }}>
      <div style={{ fontSize: 12, color: '#6b6a78', textTransform: 'uppercase',
                    letterSpacing: .4 }}>{titulo}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: '#2a2a32', lineHeight: 1.2 }}>{valor}</div>
      {nota && <div style={{ fontSize: 12, color: '#6b6a78' }}>{nota}</div>}
    </div>
  );
}

function agendamentoTexto(r) {
  if (!r.schedule_id) return '—';
  if (!r.schedule_ativo) return 'suspenso';
  const dias = String(r.weekdays || '').split(',').filter(Boolean)
    .map((d) => DIAS[Number(d)] || d).join(', ');
  const ate = r.ends_on ? ` · até ${fmtData(r.ends_on, false)}` : '';
  return `${dias} às ${String(r.hour_brt).padStart(2, '0')}h${ate}`;
}

// Abaixo disto a tabela de 10 colunas não cabe e CORTA EM SILÊNCIO — a coluna some e
// parece dado faltando, que foi exatamente o que aconteceu ao abrir no celular.
const ESTREITO = '(max-width: 820px)';

function useEstreito() {
  const [estreito, setEstreito] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(ESTREITO).matches);
  useEffect(() => {
    const mq = window.matchMedia(ESTREITO);
    const ouvir = (e) => setEstreito(e.matches);
    mq.addEventListener ? mq.addEventListener('change', ouvir) : mq.addListener(ouvir);
    return () => (mq.removeEventListener
      ? mq.removeEventListener('change', ouvir) : mq.removeListener(ouvir));
  }, []);
  return estreito;
}

// Modal próprio, sem biblioteca. Fecha no Esc e no clique fora, e tranca o scroll do fundo
// enquanto está aberto — sem isso a página atrás rola junto no celular e a pessoa perde o
// lugar onde estava.
function Modal({ titulo, subtitulo, largura = 560, onFechar, children, acoes }) {
  useEffect(() => {
    const tecla = (e) => { if (e.key === 'Escape') onFechar(); };
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', tecla);
    return () => {
      document.body.style.overflow = antes;
      window.removeEventListener('keydown', tecla);
    };
  }, [onFechar]);

  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onFechar(); }}
         style={{ position: 'fixed', inset: 0, background: 'rgba(24,23,30,.45)', zIndex: 1100,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label={titulo}
           style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: largura,
                    maxHeight: '86vh', display: 'flex', flexDirection: 'column',
                    boxShadow: '0 16px 48px rgba(24,23,30,.24)' }}>
        <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid #f2f2f5' }}>
          <h3 style={{ margin: 0, fontSize: 17, color: '#2a2a32' }}>{titulo}</h3>
          {subtitulo && (
            <div style={{ marginTop: 3, fontSize: 13, color: '#6b6a78' }}>{subtitulo}</div>
          )}
        </div>
        <div style={{ padding: '14px 20px', overflowY: 'auto' }}>{children}</div>
        <div style={{ padding: '12px 20px 16px', borderTop: '1px solid #f2f2f5',
                      display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {acoes}
        </div>
      </div>
    </div>
  );
}

const btBase = { borderRadius: 8, padding: '8px 14px', fontSize: 14, cursor: 'pointer' };
const btNeutro = { ...btBase, border: '1px solid #e8e7ee', background: '#fff', color: '#44434d' };
const btPrimario = { ...btBase, border: 0, background: '#2a2a32', color: '#fff' };
const btPerigo = { ...btBase, border: 0, background: '#b3261e', color: '#fff' };

function Aviso({ texto, detalhe }) {
  if (!texto) return null;
  return (
    <div style={{ background: '#fdecea', border: '1px solid #f5c6c2', borderRadius: 8,
                  padding: '10px 12px', marginBottom: 12, fontSize: 13, color: '#8c1d18' }}>
      <div style={{ fontWeight: 600 }}>{texto}</div>
      {detalhe?.o_que_fazer && <div style={{ marginTop: 4 }}>{detalhe.o_que_fazer}</div>}
      {(detalhe?.ficam_de_fora || []).map((p, i) => (
        <div key={i} style={{ marginTop: 2 }}>{p.nome} — {p.motivo}</div>
      ))}
      {(detalhe?.quem || []).map((p, i) => (
        <div key={i} style={{ marginTop: 2 }}>{p.nome}</div>
      ))}
    </div>
  );
}

// Quem recebe o agendamento. As caixas mostram o VEREDITO (quem de fato receberia hoje), e
// o que se grava são os DELTAS sobre a regra de papel — não a lista fechada. É essa
// diferença que faz um gerente que entrar mês que vem continuar entrando sozinho.
function ModalDestinatarios({ r, onFechar, onSalvo }) {
  const [dados, setDados] = useState(null);
  const [marcados, setMarcados] = useState(null);
  const [erro, setErro] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let vivo = true;
    apiService.getWhatsappDestinatarios(r.schedule_id).then((res) => {
      if (!vivo) return;
      if (!res.success) { setErro(res.message || 'Não consegui carregar a lista.'); return; }
      setDados(res.data);
      setMarcados(new Set((res.data.candidatos || [])
        .filter((c) => c.recebe).map((c) => c.user_id)));
    });
    return () => { vivo = false; };
  }, [r.schedule_id]);

  const alternar = (c) => {
    if (c.motivo) return;
    setMarcados((atual) => {
      const novo = new Set(atual);
      if (novo.has(c.user_id)) novo.delete(c.user_id); else novo.add(c.user_id);
      return novo;
    });
  };

  const salvar = async () => {
    setSalvando(true); setErro(null); setDetalhe(null);
    const incluir = [];
    const excluir = [];
    for (const c of dados.candidatos) {
      if (c.motivo) {
        // Caixa desligada não é escolha: quem está sem telefone ou sem opt-in mantém o que
        // já estava gravado. Derivar do visual aqui EXCLUIRIA para sempre quem só está sem
        // telefone hoje — e ninguém descobriria isso ao cadastrar o número depois.
        if (c.incluido) incluir.push(c.user_id);
        if (c.excluido) excluir.push(c.user_id);
        continue;
      }
      if (marcados.has(c.user_id) && !c.na_regra) incluir.push(c.user_id);
      if (!marcados.has(c.user_id) && c.na_regra) excluir.push(c.user_id);
    }
    const res = await apiService.salvarWhatsappDestinatarios({
      subdomain: r.subdomain, schedule_id: r.schedule_id, incluir, excluir });
    setSalvando(false);
    if (!res.success) { setErro(res.message); setDetalhe(res.details); return; }
    onSalvo();
  };

  const candidatos = dados?.candidatos || [];
  const quantos = marcados ? candidatos.filter((c) => marcados.has(c.user_id)).length : 0;

  return (
    <Modal titulo={`Destinatários · ${String(r.trigger_word || '').toUpperCase()}`}
           subtitulo={`${r.empresa || r.subdomain} · ${agendamentoTexto(r)}`}
           largura={620} onFechar={onFechar}
           acoes={
             <>
               <button style={btNeutro} onClick={onFechar}>Cancelar</button>
               <button style={{ ...btPrimario, opacity: dados && !salvando ? 1 : .5 }}
                       disabled={!dados || salvando} onClick={salvar}>
                 {salvando ? 'Salvando…' : `Salvar (${quantos})`}
               </button>
             </>
           }>
      <Aviso texto={erro} detalhe={detalhe} />
      {!dados && !erro && <div style={{ color: '#6b6a78' }}>Carregando…</div>}
      {dados && candidatos.length === 0 && (
        <div style={{ color: '#6b6a78' }}>
          Ninguém desta empresa pode receber relatório. Só diretor, gerente e supervisor
          recebem — e cada um precisa de telefone e opt-in na tela de usuários.
        </div>
      )}
      {candidatos.map((c) => (
        <label key={c.user_id}
               style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
                        padding: '9px 4px', borderBottom: '1px solid #f6f6f8',
                        cursor: c.motivo ? 'not-allowed' : 'pointer',
                        opacity: c.motivo ? .6 : 1 }}>
          <input type="checkbox" disabled={!!c.motivo}
                 checked={!!marcados?.has(c.user_id)} onChange={() => alternar(c)}
                 style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }} />
          <span style={{ flex: 1 }}>
            <span style={{ color: '#2a2a32', fontSize: 14 }}>{c.nome}</span>
            <span style={{ color: '#6b6a78', fontSize: 12, marginLeft: 8 }}>{c.papel}</span>
            {!c.na_regra && !c.motivo && (
              <span style={{ color: '#8a5a00', fontSize: 11, marginLeft: 8 }}>
                fora da regra de papel
              </span>
            )}
            <div style={{ color: '#9a99a6', fontSize: 12, fontFamily: 'monospace' }}>
              {c.telefone || '—'}
            </div>
            {c.motivo && (
              <div style={{ color: '#b3261e', fontSize: 12 }}>
                {c.motivo}
                {(c.motivo.includes('telefone') || c.motivo.includes('opt-in')) &&
                  ' — resolve na tela de usuários'}
              </div>
            )}
          </span>
        </label>
      ))}
    </Modal>
  );
}

// Apagar. Os dois alvos moram no mesmo modal porque a pergunta real é "apagar o quê?", e
// separar em dois botões faria alguém apagar o relatório inteiro querendo só parar o
// disparo de segunda.
function ModalApagar({ r, onFechar, onFeito }) {
  const temAgenda = !!r.schedule_id;
  const [alvo, setAlvo] = useState(temAgenda ? 'agendamento' : 'relatorio');
  const [erro, setErro] = useState(null);
  const [indo, setIndo] = useState(false);

  const apagar = async () => {
    setIndo(true); setErro(null);
    const res = alvo === 'agendamento'
      ? await apiService.apagarWhatsappAgendamento({ subdomain: r.subdomain,
                                                     schedule_id: r.schedule_id })
      : await apiService.apagarWhatsappRelatorio({ subdomain: r.subdomain, id: r.id });
    setIndo(false);
    if (!res.success) { setErro(res.message || 'Não consegui apagar.'); return; }
    onFeito();
  };

  const opcao = (valor, titulo, texto) => (
    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0',
                    cursor: 'pointer' }}>
      <input type="radio" name="alvo" checked={alvo === valor} value={valor}
             onChange={() => setAlvo(valor)}
             style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }} />
      <span>
        <div style={{ color: '#2a2a32', fontSize: 14, fontWeight: 600 }}>{titulo}</div>
        <div style={{ color: '#6b6a78', fontSize: 13 }}>{texto}</div>
      </span>
    </label>
  );

  return (
    <Modal titulo={`Apagar · ${String(r.trigger_word || '').toUpperCase()}`}
           subtitulo={`${r.empresa || r.subdomain} · ${r.display_name}`}
           onFechar={onFechar}
           acoes={
             <>
               <button style={btNeutro} onClick={onFechar}>Cancelar</button>
               <button style={{ ...btPerigo, opacity: indo ? .6 : 1 }} disabled={indo}
                       onClick={apagar}>
                 {indo ? 'Apagando…' : (alvo === 'agendamento' ? 'Apagar agendamento'
                                                               : 'Apagar relatório')}
               </button>
             </>
           }>
      <Aviso texto={erro} />
      {temAgenda && opcao('agendamento', 'Só o agendamento',
        'Para de disparar e some da tela. O relatório continua existindo e respondendo a '
        + 'quem digitar a palavra no WhatsApp.')}
      {opcao('relatorio', 'O relatório inteiro',
        (temAgenda ? 'Leva o agendamento junto. ' : '')
        + 'A palavra deixa de responder no WhatsApp e fica livre para ser usada de novo. '
        + 'Criar de volta é pelo MCP, com teste e aprovação outra vez.')}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f2f2f5',
                    fontSize: 12, color: '#6b6a78' }}>
        O histórico de mensagens enviadas continua no banco — o que sai é a configuração, não
        a prova do que foi entregue.
      </div>
    </Modal>
  );
}

function Destinatarios({ r, compacto }) {
  const [abrirFora, setAbrirFora] = useState(false);
  const dentro = r.destinatarios_lista || [];
  const fora = r.fora_lista || [];
  if (!r.schedule_id || !r.schedule_ativo) return <span style={{ color: '#6b6a78' }}>—</span>;
  return (
    <div style={{ fontSize: compacto ? 13 : 13 }}>
      {dentro.length === 0 && <span style={{ color: '#b3261e' }}>ninguém elegível</span>}
      {dentro.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'baseline',
                              padding: '1px 0' }}>
          <span style={{ color: '#2a2a32' }}>{p.nome}</span>
          <span style={{ color: '#6b6a78', fontSize: 11 }}>{p.papel}</span>
          <span style={{ color: '#9a99a6', fontSize: 11, fontFamily: 'monospace' }}>
            {p.telefone}
          </span>
          {/* Janela aberta = a entrega sai de graça. É informação de custo, não enfeite. */}
          {p.janela_aberta && (
            <span title="janela de 24h aberta: entrega sem custo"
                  style={{ color: '#0f7a2e', fontSize: 11 }}>· grátis</span>
          )}
        </div>
      ))}
      {(r.removidos_lista || []).map((p, i) => (
        <div key={`rm${i}`} style={{ display: 'flex', gap: 6, alignItems: 'baseline',
                                     padding: '1px 0', color: '#9a99a6' }}>
          <span style={{ textDecoration: 'line-through' }}>{p.nome}</span>
          <span style={{ fontSize: 11 }}>removido à mão</span>
        </div>
      ))}
      {fora.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <button onClick={() => setAbrirFora((v) => !v)}
                  style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer',
                           color: '#6b6a78', fontSize: 12, textDecoration: 'underline' }}>
            {abrirFora ? 'ocultar' : `${fora.length} ficam de fora`}
          </button>
          {abrirFora && (
            <div style={{ marginTop: 4 }}>
              {fora.map((p, i) => (
                <div key={i} style={{ color: '#6b6a78', fontSize: 12, padding: '1px 0' }}>
                  {p.nome} <span style={{ fontSize: 11 }}>{p.papel}</span> — {p.motivo}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Linha({ rotulo, valor }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '3px 0', fontSize: 14 }}>
      <span style={{ color: '#6b6a78', minWidth: 104, flexShrink: 0 }}>{rotulo}</span>
      <span style={{ color: '#2a2a32' }}>{valor}</span>
    </div>
  );
}

function Acoes({ r, onDestinatarios, onApagar, empilhado }) {
  const bt = { border: '1px solid #e8e7ee', background: '#fff', borderRadius: 8,
               padding: '5px 10px', fontSize: 12, cursor: 'pointer', color: '#44434d',
               whiteSpace: 'nowrap' };
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap',
                  flexDirection: empilhado ? 'row' : 'column', alignItems: 'flex-start' }}>
      {r.schedule_id && (
        <button style={bt} onClick={() => onDestinatarios(r)}>Destinatários</button>
      )}
      <button style={{ ...bt, color: '#b3261e' }} onClick={() => onApagar(r)}>Apagar</button>
    </div>
  );
}

function Cartao({ r, onDestinatarios, onApagar }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e7ee', borderRadius: 10,
                  padding: 14, marginBottom: 10, opacity: r.active ? 1 : .55 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: 16 }}>
          <LinkDash r={r}>{String(r.trigger_word || '').toUpperCase()}</LinkDash>
        </strong>
        <Selo status={r.status} />
        {!r.active && <span style={{ fontSize: 12, color: '#6b6a78' }}>desativado</span>}
      </div>
      <div style={{ color: '#44434d', marginBottom: 8 }}>
        <LinkDash r={r}>{r.display_name}</LinkDash>
      </div>
      <Linha rotulo="Agendamento" valor={agendamentoTexto(r)} />
      <Linha rotulo="Próximo" valor={r.schedule_ativo ? fmtData(r.next_run) : '—'} />
      <div style={{ padding: '6px 0' }}>
        <div style={{ color: '#6b6a78', fontSize: 14, marginBottom: 2 }}>
          Destinatários {r.destinatarios_agora != null ? `(${r.destinatarios_agora})` : ''}
        </div>
        <Destinatarios r={r} compacto />
      </div>
      <Linha rotulo="Envios" valor={`${r.envios ?? 0}${r.envios ? ` · ${r.lidas ?? 0} lidas` : ''}`} />
      <Linha rotulo="Aprovado por" valor={r.approved_by || '—'} />
      <Linha rotulo="Cadastrou" valor={r.author_email || '—'} />
      <div style={{ marginTop: 10 }}>
        <Acoes r={r} onDestinatarios={onDestinatarios} onApagar={onApagar} empilhado />
      </div>
    </div>
  );
}

export default function WhatsappRelatorios() {
  const estreito = useEstreito();
  const [dados, setDados] = useState(null);
  const [msgs, setMsgs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [modal, setModal] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    const [a, b] = await Promise.all([
      apiService.getWhatsappRelatorios(),
      apiService.getWhatsappMensagens({ limite: 100, dias: 30 }),
    ]);
    if (!a.success) setErro(a.message || 'Não consegui carregar os relatórios.');
    else setDados(a.data);
    if (b.success) setMsgs(b.data);
    setLoading(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const fechar = useCallback(() => setModal(null), []);
  const feito = useCallback(() => { setModal(null); carregar(); }, [carregar]);
  const abrirDestinatarios = useCallback((r) => setModal({ tipo: 'dest', r }), []);
  const abrirApagar = useCallback((r) => setModal({ tipo: 'apagar', r }), []);

  if (loading) return <div style={{ padding: 32, color: '#6b6a78' }}>Carregando…</div>;
  if (erro) return <div style={{ padding: 32, color: '#b3261e' }}>{erro}</div>;

  const itens = dados?.itens || [];
  const aprovados = itens.filter((r) => r.status === 'approved').length;
  const agendados = itens.filter((r) => r.schedule_id && r.schedule_ativo).length;
  const resumo = msgs?.resumo || {};

  // Agrupa por empresa: a pergunta é sempre "o que este cliente tem", nunca uma lista chapada.
  const porEmpresa = itens.reduce((acc, r) => {
    (acc[r.empresa || r.subdomain] = acc[r.empresa || r.subdomain] || []).push(r);
    return acc;
  }, {});

  const th = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: .4,
               color: '#6b6a78', padding: '8px 10px', borderBottom: '1px solid #e8e7ee',
               whiteSpace: 'nowrap' };
  const td = { padding: '10px', borderBottom: '1px solid #f2f2f5', fontSize: 14,
               color: '#2a2a32', verticalAlign: 'top' };

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1400 }}>
      {modal?.tipo === 'dest' && (
        <ModalDestinatarios r={modal.r} onFechar={fechar} onSalvo={feito} />
      )}
      {modal?.tipo === 'apagar' && (
        <ModalApagar r={modal.r} onFechar={fechar} onFeito={feito} />
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: '#2a2a32' }}>WhatsApp · Relatórios</h1>
        <button onClick={carregar}
                style={{ marginLeft: 'auto', border: '1px solid #e8e7ee', background: '#fff',
                         borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
                         fontSize: 13, color: '#44434d' }}>Atualizar</button>
      </div>
      <p style={{ margin: '0 0 20px', color: '#6b6a78', fontSize: 14 }}>
        Daqui dá para apagar e mexer em quem recebe. Cadastro, teste, aprovação e
        agendamento acontecem pelo MCP.
      </p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <Tile titulo="Relatórios" valor={itens.length} nota={`${aprovados} aprovados`} />
        <Tile titulo="Agendados" valor={agendados} nota="com disparo ativo" />
        <Tile titulo="Enviadas (30d)" valor={resumo.enviadas ?? '—'}
              nota={`${resumo.cobradas ?? 0} cobradas`} />
        <Tile titulo="Leitura" valor={resumo.taxa_leitura != null ? `${resumo.taxa_leitura}%` : '—'}
              nota={`${resumo.lidas ?? 0} de ${resumo.enviadas ?? 0} abertas`} />
      </div>

      {Object.keys(porEmpresa).length === 0 && (
        <p style={{ color: '#6b6a78' }}>Nenhum relatório configurado ainda.</p>
      )}

      {Object.entries(porEmpresa).map(([empresa, rs]) => (
        <div key={empresa} style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 15, color: '#44434d', margin: '0 0 8px' }}>{empresa}</h2>
          {estreito ? rs.map((r) => (
            <Cartao key={r.id} r={r} onDestinatarios={abrirDestinatarios}
                    onApagar={abrirApagar} />
          )) : (
          <div style={{ background: '#fff', border: '1px solid #e8e7ee', borderRadius: 10,
                        overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Palavra</th>
                  <th style={th}>Relatório</th>
                  <th style={th}>Situação</th>
                  <th style={th}>Aprovado por</th>
                  <th style={th}>Agendamento</th>
                  <th style={th}>Próximo</th>
                  <th style={th}>Destinatários</th>
                  <th style={{ ...th, textAlign: 'right' }}>Envios</th>
                  <th style={{ ...th, textAlign: 'right' }}>Lidas</th>
                  <th style={th}>Cadastrou</th>
                  <th style={th}> </th>
                </tr>
              </thead>
              <tbody>
                {rs.map((r) => (
                  <tr key={r.id} style={{ opacity: r.active ? 1 : .5 }}>
                    <td style={{ ...td, fontWeight: 600 }}>
                      <LinkDash r={r}>{String(r.trigger_word || '').toUpperCase()}</LinkDash>
                      {!r.active && <div style={{ fontSize: 11, color: '#6b6a78' }}>desativado</div>}
                    </td>
                    <td style={td}><LinkDash r={r}>{r.display_name}</LinkDash></td>
                    <td style={td}><Selo status={r.status} /></td>
                    <td style={td}>
                      {r.approved_by || '—'}
                      {r.approved_at && (
                        <div style={{ fontSize: 11, color: '#6b6a78' }}>{fmtData(r.approved_at)}</div>
                      )}
                    </td>
                    <td style={td}>{agendamentoTexto(r)}</td>
                    <td style={td}>{r.schedule_ativo ? fmtData(r.next_run) : '—'}</td>
                    <td style={{ ...td, minWidth: 230 }}><Destinatarios r={r} /></td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {r.envios ?? 0}
                      {r.ultimo_envio && (
                        <div style={{ fontSize: 11, color: '#6b6a78' }}>{fmtData(r.ultimo_envio)}</div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {r.lidas ?? 0}
                      {r.envios > 0 && (
                        <div style={{ fontSize: 11, color: '#6b6a78' }}>
                          {Math.round(100 * (r.lidas || 0) / r.envios)}%
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, fontSize: 12, color: '#6b6a78' }}>
                      {r.author_email || '—'}
                      <div>{fmtData(r.created, false)}</div>
                    </td>
                    <td style={td}>
                      <Acoes r={r} onDestinatarios={abrirDestinatarios}
                             onApagar={abrirApagar} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      ))}

      <h2 style={{ fontSize: 15, color: '#44434d', margin: '32px 0 8px' }}>
        Últimas mensagens <span style={{ fontWeight: 400, color: '#6b6a78' }}>(30 dias)</span>
      </h2>
      <div style={{ background: '#fff', border: '1px solid #e8e7ee', borderRadius: 10,
                    overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse',
                        minWidth: estreito ? 720 : undefined }}>
          <thead>
            <tr>
              <th style={th}>Quando</th>
              <th style={th}>Cliente</th>
              <th style={th}>Pessoa</th>
              <th style={th}>Direção</th>
              <th style={th}>Origem</th>
              <th style={th}>Conteúdo</th>
              <th style={th}>Situação</th>
              <th style={th}>Lida</th>
              <th style={th}>Cobrada</th>
            </tr>
          </thead>
          <tbody>
            {(msgs?.itens || []).map((m) => (
              <tr key={m.id}>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtData(m.created)}</td>
                <td style={td}>{m.empresa || m.subdomain || '—'}</td>
                <td style={td}>
                  {m.pessoa || '—'}
                  {m.papel && <div style={{ fontSize: 11, color: '#6b6a78' }}>{m.papel}</div>}
                </td>
                <td style={td}>{m.direction === 'in' ? 'recebida' : 'enviada'}</td>
                <td style={td}>{m.source}{m.kind ? ` · ${m.kind}` : ''}</td>
                <td style={{ ...td, maxWidth: 320 }}>{m.body || '—'}</td>
                <td style={td}>
                  {m.status}
                  {m.error_code && (
                    <div style={{ fontSize: 11, color: '#b3261e' }}>
                      {m.error_code} {m.error_detail || ''}
                    </div>
                  )}
                </td>
                <td style={td}>{m.read_at ? fmtData(m.read_at) : '—'}</td>
                <td style={td}>{m.direction === 'out' ? (m.billable ? 'sim' : 'não') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
